import React, { MouseEvent } from "react";
import { Form, Divider } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styled from "styled-components";
import { convertToRaw, ContentState } from "draft-js";
import useAuthorizedContext from "hooks/useAuthorizedContext";

import FormTextInput from "../../molecules/FormFields/FormTextInput";
import FormSelect from "../../molecules/FormFields/FormSelect";
import useForm from "../../../hooks/useForm";
import FormGrid from "../../templates/FormGrid";
import FormImageZone from "../../molecules/FormFields/FormImageZone";
import FormGeolocationFields from "../../molecules/FormFields/FormGeolocationInput";
import { OrganizationCategoryOpts } from "../../../constants/organizationCategories";
import { definedRouteTypes, definedErrors } from "../../../api/api";
import {
  updateOrganizations,
  postOrganizations,
  getSingleOrganization,
} from "../../../api/OrganizationsApi";
import FormTextArray from "../../molecules/FormFields/FormArrayField";
import Typography from "../../atoms/Typography/Typography";
import { definedTypographyTypes } from "../../atoms/Typography/TypographyStyles";
import SimpleButton, {
  definedButtonTypes,
} from "../../atoms/Buttons/SimpleButton/SimpleButton";
import { IOrganizationForm } from "../../../api";
import FormTextArea from "../../molecules/FormFields/FormTextArea";
import FormRadioButton from "../../molecules/FormFields/FormRadioButton";
import FormSocialFields from "../../molecules/FormFields/FormSocialFields";
import FormMultiSelect from "../../molecules/FormFields/FormMultiSelect";
import { IBaseFormProps } from "../../../interfaces/forms";
import FormRichTextArea from "../../molecules/FormFields/FormRichTextArea";
import useLocalizedCountryOpts from "../../../hooks/useLocalizedCountryOpts";
import useTypeTableContext, {
  definedTableActions,
} from "../../../hooks/useTypeTableContext";
import EntityPostModel from "../../../models/EntityPostModel";
import { slugifyWord } from "../../../helpers/globalFunctions";

interface OrganizationsFormProps extends IBaseFormProps {
  update?: boolean;
  initialValues?: IOrganizationForm;
  onFormClose?: (event?: MouseEvent) => void;
  type?: definedRouteTypes.organizations;
}

const StyledDiv = styled.div({
  textAlign: "center",
});

const defaultInitValues: IOrganizationForm = {
  title: "",
  description: convertToRaw(ContentState.createFromText("")),
  imagePointer: undefined,
  subCategories: [],
  imageUrl: "",
  category: undefined,
  organizationId: "",
  slug: "",
  countries: [],
  address: undefined,
  type: definedRouteTypes.organizations,
  email: [],
  phone: [],
  diasporeMedia: false,
  highlighted: false,
  highlightedInCountry: false,
  partner: false,
  show: true,
  displayInHomePage: false,
  media: {},
  contactPerson: "",
  comment: "",
};

const OrganizationsForm: React.FC<OrganizationsFormProps> = ({
  initialValues,
  update,
  onFormClose,
}) => {
  const { t } = useTranslation();
  const CountryOptions = useLocalizedCountryOpts();
  const typeTableContext = useTypeTableContext();
  const { user, token } = useAuthorizedContext();
  const submitFormQuery = async (form: IOrganizationForm) => {
    try {
      const act = update ? definedTableActions.update : definedTableActions.add;
      const postForm = await EntityPostModel(form, user);

      const idToken = await token();
      if (update) {
        await updateOrganizations(idToken, postForm);
      } else {
        const newOrg = await getSingleOrganization(postForm.slug);
        if (newOrg?.slug) throw new Error(definedErrors.slugExists);
        await postOrganizations(idToken, postForm);
      }

      toast.success(
        t(update ? "Toasts.EditsSuccess" : "Toasts.Publish.organizacijas"),
      );
      if (typeTableContext) typeTableContext.updateTableEntry(postForm, act);
      if (onFormClose) onFormClose();
    } catch (err) {
      if ((err as any).message.includes(definedErrors.slugExists)) {
        toast.error(t("Toasts.OrgExists"));
      } else {
        toast.error(t("Toasts.GenericError"));
      }
      console.error("postOrganizations failed", err);
    }
  };

  const {
    values,
    submitForm,
    changeFormValue,
    changeFormImageValue,
    isLoading,
  } = useForm(initialValues || defaultInitValues, submitFormQuery);
  const {
    title,
    description,
    phone,
    shortTitle,
    email,
    website,
    category,
    countries,
    address,
    diasporeMedia,
    highlighted,
    highlightedInCountry,
    partner,
    show,
    bankCredentials,
    imageUrl,
    displayInHomePage,
    subCategories,
    contactPerson,
    comment,
  } = values;
  const media = values.media || {};

  const convertedShow = Boolean(Number(show));
  const convertedDisplay = Boolean(Number(displayInHomePage));

  const validateSlug = async () => {
    if (update) return;
    try {
      const newSlug = slugifyWord(title);
      const newOrg = await getSingleOrganization(newSlug);
      if (newOrg?.slug) toast.error(t("Toasts.CompanyExists"));
    } catch (err) {
      console.error("something wrong with slug checking", err);
    }
  };
  return (
    <Form name="editorialNewsForm" loading={isLoading}>
      <Typography type={definedTypographyTypes.h1} as="span">
        {update ? t("Forms.Titles.EditOrg") : t("Forms.Titles.AddOrg")}
      </Typography>
      <Divider />
      <FormGrid label={t("Administrēšana")}>
        <FormRadioButton
          id="show"
          label="Redzams portālā"
          value={convertedShow}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="displayInHomePage"
          label="Rādīt Sākumlapā"
          value={convertedDisplay}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="highlighted"
          label="Izcelts"
          value={highlighted}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="highlightedInCountry"
          label="Izcelts valstī"
          value={highlightedInCountry}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="partner"
          label="Partneris"
          value={partner}
          onChange={changeFormValue}
        />
        <FormRadioButton
          id="diasporeMedia"
          label="Diasporas medijs"
          value={diasporeMedia}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Galvenie Dati")}>
        <FormTextInput
          id="title"
          required
          value={title}
          label={t("Organizācijas pilnais nosaukums")}
          onChange={changeFormValue}
          onBlur={validateSlug}
        />
        <FormTextInput
          id="shortTitle"
          required
          value={shortTitle}
          label={t("Organizācijas saīsinātais nosaukums / iniciāļi")}
          onChange={changeFormValue}
        />
        {/* <FormTextInput
          id="slug"
          value={slug}
          label={t("slug")}
          onChange={changeFormValue}
        /> */}
        <FormRichTextArea
          id="description"
          required
          label={t("Īss apraksts")}
          value={description}
          onChange={changeFormValue}
          toolbarHidden
          description="Lauku izmanto tikai, lai uzlabotu atklājamību interneta meklētajos."
        />
        <FormImageZone
          id="imageUrl"
          required
          initialImgUrl={imageUrl}
          label={t("Logo")}
          onChange={changeFormValue}
          onImageChange={changeFormImageValue}
        />
      </FormGrid>
      <FormGrid label={t("Kategorija")}>
        <FormSelect
          label={t("Kategorija")}
          id="category"
          required
          value={category}
          options={OrganizationCategoryOpts}
          onChange={changeFormValue}
          search
        />
        <FormMultiSelect
          id="countries"
          label={t("Valstis")}
          options={CountryOptions}
          value={countries}
          onChange={changeFormValue}
          search
        />
        {/* // subCategories HACK sorry :( */}
        <FormRadioButton
          id="subCategories"
          label="Latvijas pēdas pasaulē"
          value={(subCategories?.includes("latvijas-pedas-pasaule") || 0) > 0}
          onChange={(id, data) => {
            const cat = "latvijas-pedas-pasaule";
            const initial = (initialValues?.subCategories || []).filter(
              (item) => item !== cat,
            );
            changeFormValue(id, data ? [...initial, cat] : initial);
          }}
        />
      </FormGrid>
      <FormGrid label={t("Adrese")}>
        <FormGeolocationFields
          id="address"
          value={address}
          onChange={changeFormValue}
        />
      </FormGrid>
      <FormGrid label={t("Kontakti")}>
        <FormTextInput
          id="website"
          label={t("Mājaslapa")}
          value={website}
          onChange={changeFormValue}
        />
        <FormTextArray
          id="email"
          label={t("E-pasts")}
          value={email}
          onChange={changeFormValue}
        />
        <FormTextArray
          id="phone"
          label={t("Telefons")}
          value={phone}
          onChange={changeFormValue}
        />
        <FormSocialFields id="media" value={media} onChange={changeFormValue} />
        <FormTextArea
          id="bankCredentials"
          label={t("Bankas rekvizīti")}
          value={bankCredentials}
          onChange={changeFormValue}
        />
        <FormTextInput
          id="contactPerson"
          label="Kontaktpersona"
          value={contactPerson}
          onChange={changeFormValue}
        />
        <FormTextArea
          id="comment"
          label="Komentārs"
          value={comment}
          onChange={changeFormValue}
        />
      </FormGrid>
      <StyledDiv>
        {onFormClose && (
          <SimpleButton
            id="FormCancel"
            variant={definedButtonTypes.secondary}
            onClick={onFormClose}
          >
            {t("cancel")}
          </SimpleButton>
        )}
        <SimpleButton
          onClick={(e) => submitForm(e)}
          variant={definedButtonTypes.primary}
          id="FormSubmit"
        >
          {t("submit")}
        </SimpleButton>
      </StyledDiv>
    </Form>
  );
};
OrganizationsForm.defaultProps = {
  type: definedRouteTypes.organizations,
};

export default OrganizationsForm;
